import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  compressPdf,
  mergePdfs,
  splitPdf,
  triggerDownload,
  triggerDownloads
} from '../../services/officeToolsApi.js';
import { rememberRecentApp } from '../../utils/recentApps.js';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiLayers,
  FiLock,
  FiMinimize2,
  FiScissors,
  FiX
} from 'react-icons/fi';
import ThemeToggle from '../ThemeToggle.jsx';
import TourButton from '../tour/TourButton.jsx';
import HelpMenu, { FeedbackButton } from '../help/HelpMenu.jsx';
import TourMode from '../tour/TourMode.jsx';
import { usePageTour } from '../tour/usePageTour.js';
import FileDrop from './FileDrop.jsx';
import '../../styles/office-tools.css';

const TABS = [
  { id: 'merge', label: 'Merge', icon: FiLayers },
  { id: 'split', label: 'Split', icon: FiScissors },
  { id: 'compress', label: 'Compress', icon: FiMinimize2 }
];

const PDF_TOUR_STEPS = [
  {
    target: 'pdf-tabs',
    title: 'Choose a tool',
    body: 'Switch between Merge, Split and Compress. Each tab keeps its own files, so you can hop around freely.'
  },
  {
    target: 'pdf-drop',
    title: 'Add your PDFs',
    body: 'Click to browse or drag PDFs onto this box. For merging, pick 2–10 files in the order you want them joined.'
  },
  {
    target: 'pdf-action',
    title: 'Run it',
    body: 'One click processes the file and starts the download. Splitting downloads one PDF per page.'
  },
  {
    target: 'pdf-privacy',
    title: 'Private by design',
    body: 'Files are processed in memory and discarded right after — nothing is saved on the server.'
  }
];

export default function PdfToolsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('merge');
  const { touring, start: startTour, end: endTour } = usePageTour();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [mergeFiles, setMergeFiles] = useState([]);
  const [splitFile, setSplitFile] = useState(null);
  const [compressFile, setCompressFile] = useState(null);

  const run = async (fn) => {
    setError('');
    setStatus('');
    setBusy(true);
    try {
      await fn();
      rememberRecentApp('pdf-tools');
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="office-tools-page">
      <header className="office-tools-topbar">
        <button type="button" className="office-tools-back" onClick={() => navigate('/apps')}>
          <FiArrowLeft aria-hidden="true" /> <span>Back to My Apps</span>
        </button>
        <div className="office-tools-topbar-actions">
          <HelpMenu onTour={startTour} />
          <FeedbackButton />
          <TourButton onClick={startTour} />
          <ThemeToggle compact />
        </div>
      </header>

      <div className="office-tools-shell">
        <header className="office-tools-header">
          <span className="office-tools-hero-icon" aria-hidden="true"><FiLayers /></span>
          <div>
            <span className="office-tools-kicker">Office tools</span>
            <h1>PDF Tools</h1>
            <p>Merge, split, or lightly compress PDFs in a couple of clicks.</p>
            <span className="office-tools-privacy" data-tour="pdf-privacy">
              <FiLock aria-hidden="true" /> Processed ephemerally — never stored
            </span>
          </div>
        </header>

        {error && (
          <div className="office-tools-alert is-error" role="alert">
            <FiAlertCircle aria-hidden="true" /> <span>{error}</span>
          </div>
        )}
        {status && !error && (
          <div className="office-tools-alert is-ok">
            <FiCheckCircle aria-hidden="true" /> <span>{status}</span>
          </div>
        )}

        <div className="office-tools-tabs" role="tablist" aria-label="PDF tool" data-tour="pdf-tabs">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={tab === item.id ? 'is-active' : ''}
              onClick={() => setTab(item.id)}
            >
              <item.icon aria-hidden="true" />
              {item.label}
            </button>
          ))}
        </div>

        <section className={`office-tools-card ${busy ? 'is-busy' : ''}`} key={tab}>
          {tab === 'merge' && (
            <>
              <h2>Merge PDFs</h2>
              <p className="office-tools-help">Select 2–10 PDF files. Order follows your selection order.</p>
              <FileDrop
                className="office-tools-file"
                data-tour="pdf-drop"
                accept="application/pdf,.pdf"
                multiple
                title={mergeFiles.length ? 'Choose different PDFs' : 'Choose PDFs'}
                hint="or drag and drop 2–10 PDFs here"
                onFiles={(files) => setMergeFiles(files)}
              />
              {mergeFiles.length > 0 && (
                <ol className="office-tools-file-list nb-stagger">
                  {mergeFiles.map((f) => (
                    <li key={`${f.name}-${f.size}`}>
                      <FiFileText aria-hidden="true" />
                      <span>{f.name}</span>
                    </li>
                  ))}
                </ol>
              )}
              <button
                type="button"
                className="office-tools-primary"
                data-tour="pdf-action"
                disabled={busy || mergeFiles.length < 2}
                onClick={() => run(async () => {
                  const result = await mergePdfs(mergeFiles);
                  triggerDownload(result);
                  setStatus(`Merged ${mergeFiles.length} files → ${result.filename}`);
                })}
              >
                {busy ? <span className="office-tools-spinner" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
                {busy ? 'Working…' : 'Merge & download'}
              </button>
            </>
          )}

          {tab === 'split' && (
            <>
              <h2>Split into pages</h2>
              <p className="office-tools-help">
                Each page becomes its own PDF and downloads separately (e.g. a 2-page file → 2 downloads).
              </p>
              <FileDrop
                className="office-tools-file"
                data-tour="pdf-drop"
                accept="application/pdf,.pdf"
                title={splitFile ? 'Choose a different PDF' : 'Choose PDF'}
                hint="or drag and drop it here"
                onFiles={(files) => setSplitFile(files[0] || null)}
              />
              {splitFile && (
                <div className="office-tools-file-row office-tools-file-chip">
                  <FiFileText aria-hidden="true" />
                  <p className="office-tools-filename">{splitFile.name}</p>
                  <button
                    type="button"
                    className="office-tools-secondary"
                    onClick={() => setSplitFile(null)}
                  >
                    <FiX aria-hidden="true" /> Remove
                  </button>
                </div>
              )}
              <button
                type="button"
                className="office-tools-primary"
                data-tour="pdf-action"
                disabled={busy || !splitFile}
                onClick={() => run(async () => {
                  const result = await splitPdf(splitFile);
                  await triggerDownloads(result.pages);
                  setStatus(
                    result.pages.length === 1
                      ? `Downloaded ${result.pages[0].filename}`
                      : `Split into ${result.pages.length} PDFs and started downloads`
                  );
                })}
              >
                {busy ? <span className="office-tools-spinner" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
                {busy ? 'Working…' : 'Split & download pages'}
              </button>
            </>
          )}

          {tab === 'compress' && (
            <>
              <h2>Compress PDF</h2>
              <p className="office-tools-help">
                Light recompression (object streams). Large image-heavy PDFs may only shrink a little.
              </p>
              <FileDrop
                className="office-tools-file"
                data-tour="pdf-drop"
                accept="application/pdf,.pdf"
                title={compressFile ? 'Choose a different PDF' : 'Choose PDF'}
                hint="or drag and drop it here"
                onFiles={(files) => setCompressFile(files[0] || null)}
              />
              {compressFile && (
                <div className="office-tools-file-chip">
                  <FiFileText aria-hidden="true" />
                  <p className="office-tools-filename">{compressFile.name}</p>
                </div>
              )}
              <button
                type="button"
                className="office-tools-primary"
                data-tour="pdf-action"
                disabled={busy || !compressFile}
                onClick={() => run(async () => {
                  const result = await compressPdf(compressFile);
                  const original = result.headers.get('X-NovaBotX-Original-Bytes');
                  const output = result.headers.get('X-NovaBotX-Output-Bytes');
                  triggerDownload(result);
                  setStatus(
                    original && output
                      ? `Compressed ${original} → ${output} bytes`
                      : `Downloaded ${result.filename}`
                  );
                })}
              >
                {busy ? <span className="office-tools-spinner" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
                {busy ? 'Working…' : 'Compress & download'}
              </button>
            </>
          )}
          {busy && <div className="office-tools-progress" aria-hidden="true" />}
        </section>
      </div>
      {touring && <TourMode steps={PDF_TOUR_STEPS} topOffset={64} onClose={endTour} />}
    </div>
  );
}
